import React, { useEffect, useState } from "react";
import supabase from "../supabaseClient";
import "../pages/CheckInLog.css"; // New CSS file

const CheckInLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carwashId, setCarwashId] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchUsername, setSearchUsername] = useState("");
  const [analytics, setAnalytics] = useState({
    total: 0,
    uniqueUsers: 0,
    dailyBreakdown: {},
  });

  useEffect(() => {
    const fetchCarwash = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { data: carwash } = await supabase
        .from("carwashes")
        .select("id, name")
        .eq("user_id", user.id)
        .single();

      if (carwash) {
        setCarwashId(carwash.id);
      }
    };

    fetchCarwash();
  }, []);

  useEffect(() => {
    const fetchLogs = async () => {
      if (!carwashId) return;

      setLoading(true);

      let query = supabase
        .from("check_ins")
        .select("*")
        .eq("carwash_id", carwashId)
        .eq("validated", true)
        .order("check_in_time", { ascending: false });

      if (startDate) {
        query = query.gte("check_in_time", `${startDate}T00:00:00`);
      }
      if (endDate) {
        query = query.lte("check_in_time", `${endDate}T23:59:59`);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Failed to fetch check-in log:", error);
      } else {
        const filtered = searchUsername
          ? data.filter((item) =>
              item.username.toLowerCase().includes(searchUsername.toLowerCase())
            )
          : data;

        setLogs(filtered);

        // Analytics calculation
        const uniqueUsers = new Set(filtered.map((l) => l.username));
        const dailyBreakdown = {};

        filtered.forEach((log) => {
          const date = new Date(log.check_in_time).toISOString().slice(0, 10);
          dailyBreakdown[date] = (dailyBreakdown[date] || 0) + 1;
        });

        setAnalytics({
          total: filtered.length,
          uniqueUsers: uniqueUsers.size,
          dailyBreakdown,
        });
      }

      setLoading(false);
    };

    fetchLogs();
  }, [carwashId, startDate, endDate, searchUsername]);

  return (
    <div className="checkin-log">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Check-In History</h3>
          <p className="card-subtitle">Validated check-ins only</p>
        </div>

        <div className="filters-container">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label>Search Username</label>
            <input
              type="text"
              value={searchUsername}
              placeholder="e.g. john"
              onChange={(e) => setSearchUsername(e.target.value)}
              className="filter-input"
            />
          </div>
        </div>
      </div>

      {!loading && logs.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Analytics Summary</h3>
          </div>
          <div className="analytics-grid">
            <div className="analytics-card">
              <div className="analytics-value">{analytics.total}</div>
              <div className="analytics-label">Total Check-Ins</div>
            </div>
            <div className="analytics-card">
              <div className="analytics-value">{analytics.uniqueUsers}</div>
              <div className="analytics-label">Unique Users</div>
            </div>
          </div>

          <div className="daily-breakdown">
            <h4>Daily Breakdown</h4>
            <div className="breakdown-chart">
              {Object.entries(analytics.dailyBreakdown)
                .sort(([a], [b]) => new Date(b) - new Date(a))
                .map(([date, count]) => (
                  <div key={date} className="breakdown-item">
                    <div className="breakdown-date">{date}</div>
                    <div className="breakdown-bar-container">
                      <div
                        className="breakdown-bar"
                        style={{
                          width: `${(count / analytics.total) * 100}%`,
                        }}
                      ></div>
                    </div>
                    <div className="breakdown-count">{count}</div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            Check-In Records {logs.length > 0 && `(${logs.length})`}
          </h3>
        </div>

        {loading ? (
          <div className="loading-spinner">Loading check-in data...</div>
        ) : logs.length === 0 ? (
          <div className="empty-state">
            {startDate || endDate || searchUsername
              ? "No check-ins match your filters"
              : "No check-in records found"}
          </div>
        ) : (
          <div className="records-table">
            <div className="table-header">
              <div className="table-cell">User</div>
              <div className="table-cell">Check-In Time</div>
              <div className="table-cell">Status</div>
            </div>
            {logs.map((log) => (
              <div key={log.id} className="table-row">
                <div className="table-cell user-cell">
                  <span className="user-avatar">
                    {log.username.charAt(0).toUpperCase()}
                  </span>
                  <span className="user-name">{log.username}</span>
                </div>
                <div className="table-cell">
                  {new Date(log.check_in_time).toLocaleString()}
                </div>
                <div className="table-cell">
                  <span className="status-badge validated">Validated</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckInLog;
